/**
 * Credit Block shared formatters and helpers.
 *
 * Extracted from CaseDetailView, HistoryDetailView, CreditReportDetailView,
 * SalesforceActivityTab, SalesforceRelatedList, CreditBlockClientDetail.
 */

import { formatEpochOrDateAsUsDateTime } from './epochDateDisplay';

// ---- Date formatters ----

/** Format ISO date or numeric epoch (s/ms) as "M/D/YYYY, h:MM AM/PM". Returns original value if unparseable, empty if falsy. */
export function fmtDateTime(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  let d: Date;
  if (typeof v === 'number') {
    d = new Date(v);
  } else {
    const s = String(v).trim();
    if (!s) return '';
    if (/^-?\d+$/.test(s)) {
      const n = Number(s);
      // 10 digits or fewer = epoch seconds; 13 digits = epoch ms
      d = new Date(s.replace('-', '').length <= 10 ? n * 1000 : n);
    } else {
      d = new Date(s);
    }
  }
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function formatAuditDateTime(v: unknown): string {
  return formatEpochOrDateAsUsDateTime(v);
}

export function formatAuditUserDate(user: unknown, date: unknown): string {
  const userText = String(user ?? '').trim();
  const dateText = formatAuditDateTime(date).trim();
  if (!userText && !dateText) return '';
  if (!dateText) return userText;
  return userText ? `${userText}, ${dateText}` : dateText;
}

/** Format ISO date as "M/D/YYYY". Returns original string if unparseable, empty if falsy. */
export function fmtDate(v: unknown): string {
  if (!v) return '';
  const s = String(v).trim();
  const d = parseDateOnlyOrIso(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

/** Format ISO date as "h:MM AM/PM" time only. */
export function fmtTime(v: unknown): string {
  if (!v) return '';
  const s = String(v);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Format ISO date as "Jan 15, 2026". */
export function fmtShortDate(v: unknown): string {
  if (!v) return '';
  const s = String(v).trim();
  const d = parseDateOnlyOrIso(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Format ISO date as "January 2026". */
export function fmtMonthYear(v: unknown): string {
  if (!v) return '';
  const s = String(v).trim();
  const d = parseDateOnlyOrIso(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Bare "YYYY-MM-DD" strings are parsed by `new Date()` as UTC midnight, which
// renders as the prior calendar day in any negative-offset timezone (e.g. SF
// stores "2025-07-16" → in PDT this renders as "7/15/2025"). Detect the
// date-only shape and construct as local-day so the calendar day matches the
// source. Full ISO timestamps fall through to default parsing.
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function parseDateOnlyOrIso(s: string): Date {
  const m = DATE_ONLY_RE.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(s);
}

// ---- Number formatters ----

/** Format number with locale thousands separator. Returns empty for null/NaN. */
export function fmtNum(v: unknown): string {
  if (v == null) return '';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : n.toLocaleString();
}

/** Format number as "$1,234.00". Returns empty for null/NaN. */
export function fmtCurrency(v: unknown): string {
  if (v == null) return '';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---- SF ID detection ----

/** Returns true if the value looks like a Salesforce 15/18-char ID (e.g. 005Nq00000CH8VJIA1). */
export function isSfId(v: string): boolean {
  return /^[a-zA-Z0-9]{15,18}$/.test(v) && /^0[0-9A-Za-z]{2}/.test(v);
}

// ---- Label mappers ----

/** Map lock/unlock request type code to display label. */
export function requestTypeLabel(t: string): string {
  switch (t) {
    case 'lock_request': return 'Lock';
    case 'unlock_request': return 'Unblock';
    case 'permanent_block': return 'Permanent Block';
    case 'permanent_unblock': return 'Permanent Unblock';
    case 'extension': return 'Extension';
    case 'special_case': return 'Special Case';
    default: return t;
  }
}

/** Map subscription status to SF-style stage label. */
export function subscriptionStageLabel(status: string, subscriptionStage?: string): string {
  const stage = subscriptionStage?.trim();
  if (stage) return stage;
  switch (status) {
    case 'active': return 'Current Client';
    case 'cancelled': return 'Cancelled';
    case 'suspended': return 'Suspended';
    default: return status;
  }
}

/** Map bureau credential status to display label. */
export function formatBureauStageDisplay(status: string | undefined): string {
  if (!status || status === 'unknown') return '—';
  if (status === 'unlocked') return 'Unblocked';
  if (status === 'locked') return 'Locked';
  if (status === 'monitored') return 'Monitored';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Map bureau key to display name. */
export function formatBureauName(bureau: string): string {
  if (bureau === 'tri_merge') return 'Tri-Merge';
  if (!bureau) return '';
  return bureau.charAt(0).toUpperCase() + bureau.slice(1);
}

/** Map status to Tailwind color classes. */
export function getStatusColorClass(status: string): string {
  switch (status) {
    case 'active': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
    case 'cancelled': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
    case 'suspended': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300';
    case 'pending': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300';
    case 'completed': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  }
}

// ---- Related list helpers ----

/** Format count badge: "10+" when exceeds pageSize, exact number otherwise. */
export function formatRelatedListCount(displayCount: number, pageSize: number): string {
  return displayCount > pageSize ? `${pageSize}+` : String(displayCount);
}

// ---- Activity helpers ----

/** Relative year label: "This Year", "Last Year", or the year number. */
export function yearLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const thisYear = now.getFullYear();
  const actYear = d.getFullYear();
  if (actYear === thisYear) return 'This Year';
  if (actYear === thisYear - 1) return 'Last Year';
  return String(actYear);
}

/** Returns true if the activity is a pending task/meeting/event (not completed). */
export function isUpcomingOrOverdue(activity: { type: string; status?: string }): boolean {
  if (activity.type !== 'task' && activity.type !== 'meeting' && activity.type !== 'event') return false;
  const status = (activity.status ?? '').toLowerCase();
  if (status === 'completed' || status === 'done') return false;
  return true;
}

/** Generate activity description based on type. */
export function actionVerb(
  type: string,
  createdByName?: string,
  contactName?: string,
  fromEmail?: string,
): string {
  const contact = contactName || 'Contact';
  const user = createdByName || 'Someone';
  switch (type) {
    case 'email':
      return `${fromEmail || user} sent an email to ${contact}`;
    case 'call':
      return `${user} had a task with ${contact}`;
    case 'task':
      return `${user} had a task with ${contact}`;
    case 'meeting':
    case 'event':
      return `${user} had an event with ${contact}`;
    case 'note':
      return `${user} added a note`;
    default:
      return `${user} logged an activity`;
  }
}

/**
 * Group items by month key "YYYY-MM", sorted newest first.
 * Items must have a `createdAt` string field.
 */
export function groupByMonth<T extends { createdAt: string }>(items: T[]): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  const sorted = [...items].sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  for (const item of sorted) {
    const d = new Date(item.createdAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const existing = groups.get(key) ?? [];
    groups.set(key, [...existing, item]);
  }
  return groups;
}
